import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  ExternalLinkDTO,
  ExternalLinkOwnerType,
  useDeleteExternalLink,
  useGetExternalLinks,
} from "@/api/external-link";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "@/components/LinksManagement";
import { HtmlCell, TextCell } from "@/components/table/CustomCell";
import { TableRowActionsMenu } from "@/components/table/TableRowActionsMenu";
import { ACTION_I18N_KEYS } from "@/components/table/action-i18n-keys";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { DataTable } from "@/components/ui/datatable";
import { Input } from "@/components/ui/input";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { ACTION_VIEW_ICON } from "@/components/table/action-icons";
import { Pencil, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import ExternalLinkDrawer from "./drawer/ExternalLinkDrawer";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import { GridCardWithActions } from "./GridCardWithActions";
import { ExternalLinkGridCard } from "./relatedLinkGridCards";

const toOwnerType = (type: LinkableType): ExternalLinkOwnerType =>
  type as ExternalLinkOwnerType;

const STORAGE_KEY = "external-links-table";

const defaultColumnVisibility: VisibilityState = {
  name: true,
  url: true,
  dateConsultation: true,
  notes: true,
  actions: true,
};

function ExternalLinkRowActions({
  item,
  ownerId,
  type,
  onDelete,
  t,
}: {
  item: ExternalLinkDTO;
  ownerId: string;
  type: LinkableType;
  onDelete: () => void;
  t: TFunction;
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const previewElements = [
    { label: t("name"), value: item.name ?? "", type: "text" as const },
    { label: t("url"), value: item.url ?? "", type: "text" as const },
    {
      label: t("dateConsultation"),
      value: item.dateConsultation ?? "",
      type: "text" as const,
    },
    {
      label: t("notes"),
      value: item.notes ?? "",
      type: "richtext" as const,
    },
  ];

  return (
    <div
      className="flex justify-end"
      onClick={(event) => event.stopPropagation()}
    >
      <ShowElements
        type="document"
        id={item.id}
        elements={previewElements}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
      />
      <ExternalLinkDrawer
        key={`drawer-${item.id}`}
        data={item}
        ownerId={ownerId}
        type={type}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
      <TableRowActionsMenu
        actions={[
          {
            id: "edit",
            label: t(ACTION_I18N_KEYS.edit),
            icon: Pencil,
            onSelect: () => setEditOpen(true),
          },
          {
            id: "preview",
            label: t(ACTION_I18N_KEYS.view),
            icon: ACTION_VIEW_ICON,
            onSelect: () => setPreviewOpen(true),
          },
          {
            id: "delete",
            label: t(ACTION_I18N_KEYS.delete),
            icon: Trash2,
            destructive: true,
            onSelect: onDelete,
          },
        ]}
      />
    </div>
  );
}

const ExternalLinks = ({ id, type }: { id: string; type: LinkableType }) => {
  const { t } = useTranslation();
  const ownerType = toOwnerType(type);
  const { data } = useGetExternalLinks(ownerType, id);
  const { mutate: deleteExternalLink } = useDeleteExternalLink(ownerType, id);

  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedPreviewId, setSelectedPreviewId] = useState<string | null>(
    null,
  );
  const [searchContent, setSearchContent] = useState("");

  const [relatedPagination, setRelatedPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    setRelatedPagination((p) => ({ ...p, page: 0 }));
  }, [searchContent]);

  const filteredRows = useMemo(() => {
    const rows = data ?? [];
    const q = searchContent.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.name ?? "").toLowerCase().includes(q) ||
        (r.url ?? "").toLowerCase().includes(q),
    );
  }, [data, searchContent]);

  useEffect(() => {
    const total = filteredRows.length;
    const pageSize = relatedPagination.pageSize;
    const maxPage = Math.max(0, Math.ceil(total / pageSize) - 1);
    if (relatedPagination.page > maxPage) {
      setRelatedPagination((p) => ({ ...p, page: maxPage }));
    }
  }, [filteredRows.length, relatedPagination.page, relatedPagination.pageSize]);

  const paginatedRows = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return filteredRows.slice(start, end);
  }, [filteredRows, relatedPagination.page, relatedPagination.pageSize]);

  const handleAddMode = useCallback(() => {
    setIsAddMode((v) => !v);
  }, []);

  const handleDelete = useCallback(
    (linkId: string) => {
      deleteExternalLink(linkId);
      setSelectedPreviewId(null);
    },
    [deleteExternalLink],
  );

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultColumnVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultColumnVisibility };
    },
  );

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });

  const tableOptions = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnsOrder,
    state: {
      columnVisibility,
      columnOrder: columnsOrder,
    },
  };

  const columns: ColumnDef<ExternalLinkDTO, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        header: t("name"),
        cell: ({ row }) => <TextCell value={row.original.name ?? ""} />,
      },
      {
        accessorKey: "url",
        header: t("url"),
        size: 280,
        cell: ({ row }) => {
          const url = row.original.url ?? "";
          if (!url) return <TextCell value="" />;
          return (
            <div className="flex min-w-0 max-w-[280px] items-center">
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="truncate text-[14px] font-[500] text-[#005D8D] underline"
              >
                {url}
              </a>
            </div>
          );
        },
      },
      {
        accessorKey: "dateConsultation",
        header: t("dateConsultation"),
        size: 140,
        cell: ({ row }) => (
          <TextCell value={row.original.dateConsultation ?? ""} />
        ),
      },
      {
        accessorKey: "notes",
        header: t("notes"),
        size: 200,
        cell: ({ row }) => <HtmlCell value={row.original.notes ?? ""} />,
      },
      createActionsColumnDef<ExternalLinkDTO>(({ row }) => (
        <ExternalLinkRowActions
          item={row.original}
          ownerId={id}
          type={type}
          onDelete={() => handleDelete(row.original.id)}
          t={t}
        />
      )),
    ],
    [handleDelete, id, t, type],
  );

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: filteredRows.length,
  };

  return (
    <div className="w-full">
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY}
        title={t("entryExternalLinks")}
        isAddMode={isAddMode}
        onBack={handleAddMode}
        onToggleAdd={handleAddMode}
        backLabel={t("relatedRecords")}
        searchInput={
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        }
        addModeTable={
          isAddMode ? (
            <div className="w-full max-w-lg rounded-md border border-slate-200 bg-slate-50/50 p-4">
              <ExternalLinkDrawer
                key="external-link-create"
                ownerId={id}
                type={type}
                open
                onOpenChange={(open) => {
                  if (!open) setIsAddMode(false);
                }}
              />
            </div>
          ) : null
        }
        listTable={
          <DataTable
            columns={columns}
            data={paginatedRows}
            pagination={relatedPaginationProps}
            storageKey={STORAGE_KEY}
            options={tableOptions}
          />
        }
        gridContent={
          <>
            {paginatedRows.map((row) => (
              <div key={row.id}>
              <GridCardWithActions
                actions={
                  <ExternalLinkRowActions
                    item={row}
                    ownerId={id}
                    type={type}
                    onDelete={() => handleDelete(row.id)}
                    t={t}
                  />
                }
              >
                <ExternalLinkGridCard
                  item={row}
                  t={t}
                  onTextClick={() => setSelectedPreviewId(row.id)}
                />
              </GridCardWithActions>
                <ShowElements
                  type="document"
                  id={row.id}
                  open={selectedPreviewId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedPreviewId(null);
                  }}
                  trigger={<span />}
                  elements={[
                    { label: t("name"), value: row.name ?? "", type: "text" },
                    { label: t("url"), value: row.url ?? "", type: "text" },
                    {
                      label: t("dateConsultation"),
                      value: row.dateConsultation ?? "",
                      type: "text",
                    },
                    {
                      label: t("notes"),
                      value: row.notes ?? "",
                      type: "richtext",
                    },
                  ]}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    </div>
  );
};

export default ExternalLinks;
